import type { IconName } from '@/components/ui/icons';
import type { User } from '@/types';
import { getMvpAccountRoleLabel } from '@/lib/auth/accountRoles';
import { resolveIdentityRoles } from '@/lib/auth/identityRoles';
import { getActiveScopedRelationships } from '@/lib/auth/scopedRelationshipResolver';
import { companyPortalHref } from '@/lib/auth/companyPortal';
import { customerRouteHref, type CustomerRouteTarget } from './canonicalRoutes';
import { surfaceFromRequestedPath } from './returnTarget';

export type NavigationContextKind = 'account' | 'company' | 'partner' | 'vendor' | 'investor';

export interface NavigationContextOption {
  key: string;
  kind: NavigationContextKind;
  resourceId: string;
  label: string;
  description: string;
  icon: IconName;
}

const STORAGE_PREFIX = 'ax1:navigation-context:v1:';

function contextKey(kind: NavigationContextKind, resourceId: string): string {
  return `${kind}:${resourceId}`;
}

function uniqueContexts(contexts: NavigationContextOption[]): NavigationContextOption[] {
  const seen = new Set<string>();
  return contexts.filter((context) => {
    if (seen.has(context.key)) return false;
    seen.add(context.key);
    return true;
  });
}

function scopedLabel(user: User, fallback: string, resourceId: string, duplicateCount: number): string {
  const companyName = user.company?.trim();
  if (companyName && duplicateCount <= 1) return companyName;
  if (companyName) return `${companyName} · ${resourceId.slice(0, 8)}`;
  return duplicateCount <= 1 ? fallback : `${fallback} · ${resourceId.slice(0, 8)}`;
}

/**
 * Build the explicit UX contexts a user may switch between. These are
 * presentation contexts only; route guards, RLS and exact-resource authority
 * remain the source of authorization.
 */
export function listNavigationContexts(user: User | null | undefined): NavigationContextOption[] {
  if (!user) return [];
  const identity = resolveIdentityRoles(user);
  const relationships = getActiveScopedRelationships(user);
  const contexts: NavigationContextOption[] = [];

  const accountRole = identity.platformRole ?? identity.accountRole;
  if (accountRole) {
    contexts.push({
      key: contextKey('account', user.workspace_id?.trim() || user.id),
      kind: 'account',
      resourceId: user.workspace_id?.trim() || user.id,
      label: user.company?.trim() || (identity.platformRole ? 'AX1 Platform' : 'Organization'),
      description: getMvpAccountRoleLabel(accountRole),
      icon: identity.platformRole ? 'server' : 'building2',
    });
  }

  const contextFacts = [
    ...relationships
      .filter((relationship) => relationship.type === 'company_representative')
      .map((relationship) => ({
        kind: 'company' as const,
        resourceId: relationship.resourceType === 'company'
          ? relationship.resourceId
          : relationship.representedCompanyId,
        fallback: 'Represented company',
        description: 'Company Portal',
        icon: 'building2' as IconName,
      })),
    ...relationships
      .filter((relationship) => relationship.type === 'partner_representative')
      .map((relationship) => ({
        kind: 'partner' as const,
        resourceId: relationship.representedCompanyId || relationship.resourceId,
        fallback: 'Partner company',
        description: 'Partner assigned work',
        icon: 'handshake' as IconName,
      })),
    ...relationships
      .filter((relationship) => relationship.type === 'vendor_representative')
      .map((relationship) => ({
        kind: 'vendor' as const,
        resourceId: relationship.representedCompanyId || relationship.resourceId,
        fallback: 'Vendor company',
        description: 'Vendor assigned work',
        icon: 'package' as IconName,
      })),
    ...relationships
      .filter((relationship) => (
        relationship.resourceType === 'investment'
        && (relationship.type === 'investor_representative' || relationship.type === 'investment_manager')
      ))
      .map((relationship) => ({
        kind: 'investor' as const,
        resourceId: relationship.resourceId,
        fallback: 'Investor portfolio',
        description: relationship.type === 'investment_manager'
          ? 'Investment management'
          : 'Investor Portal',
        icon: 'trendingUp' as IconName,
      })),
  ].filter((fact): fact is Exclude<typeof fact, { resourceId: null | undefined }> & { resourceId: string } => (
    Boolean(fact.resourceId)
  ));

  for (const fact of contextFacts) {
    const duplicateCount = contextFacts.filter((candidate) => candidate.kind === fact.kind).length;
    contexts.push({
      key: contextKey(fact.kind, fact.resourceId),
      kind: fact.kind,
      resourceId: fact.resourceId,
      label: scopedLabel(user, fact.fallback, fact.resourceId, duplicateCount),
      description: fact.description,
      icon: fact.icon,
    });
  }

  return uniqueContexts(contexts);
}

function routeContextKey(pathname: string, contexts: NavigationContextOption[]): string | null {
  const companyMatch = pathname.match(/^\/companies\/([^/]+)(?:\/|$)/);
  if (companyMatch?.[1]) {
    const resourceId = decodeURIComponent(companyMatch[1]);
    return contexts.find((context) => context.kind === 'company' && context.resourceId === resourceId)?.key ?? null;
  }
  if (pathname === '/investor' || pathname.startsWith('/investor/')) {
    return contexts.find((context) => context.kind === 'investor')?.key ?? null;
  }
  if (pathname === '/platform' || pathname.startsWith('/platform/')) {
    return contexts.find((context) => context.kind === 'account')?.key ?? null;
  }
  return null;
}

function defaultContext(user: User, contexts: NavigationContextOption[]): NavigationContextOption | null {
  const identity = resolveIdentityRoles(user);
  const account = contexts.find((context) => context.kind === 'account') ?? null;
  if (identity.platformRole || identity.accountRole === 'organization_owner' || identity.accountRole === 'organization_admin') {
    return account ?? contexts[0] ?? null;
  }
  const portalContexts = contexts.filter((context) => context.kind !== 'account');
  if (portalContexts.length === 1) return portalContexts[0];
  return account ?? portalContexts[0] ?? null;
}

export function readRememberedNavigationContext(userId: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(`${STORAGE_PREFIX}${userId}`);
  } catch {
    return null;
  }
}

export function rememberNavigationContext(userId: string, key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${userId}`, key);
    window.dispatchEvent(new CustomEvent('ax1:navigation-context-changed', { detail: { userId, key } }));
  } catch {
    // Storage may be disabled; the destination route still carries context.
  }
}

export function resolveActiveNavigationContext(
  user: User | null | undefined,
  pathname: string,
  search = '',
  rememberedKey?: string | null,
): NavigationContextOption | null {
  if (!user) return null;
  const contexts = listNavigationContexts(user);
  if (contexts.length === 0) return null;

  const routeKey = routeContextKey(pathname, contexts);
  if (routeKey) return contexts.find((context) => context.key === routeKey) ?? null;

  const requestedKey = new URLSearchParams(search).get('context');
  if (requestedKey) {
    const requested = contexts.find((context) => context.key === requestedKey);
    if (requested) return requested;
  }

  const stored = rememberedKey ?? readRememberedNavigationContext(user.id);
  if (stored) {
    const remembered = contexts.find((context) => context.key === stored);
    if (remembered) return remembered;
  }

  return defaultContext(user, contexts);
}

const ACCOUNT_SURFACES: ReadonlySet<string> = new Set([
  'dashboard', 'projects', 'milestones', 'tasks', 'assets', 'documents',
  'companies', 'reports', 'investments', 'decisions', 'users', 'security', 'settings',
]);

export function navigationContextLandingHref(
  context: NavigationContextOption,
  options: { workspaceId?: string | null; currentPath?: string | null } = {},
): string {
  const currentSurface = surfaceFromRequestedPath(options.currentPath) ?? 'dashboard';

  if (context.kind === 'company') {
    const portalSurface = ['projects', 'tasks', 'assets', 'reports', 'decisions'].includes(currentSurface)
      ? currentSurface as 'projects' | 'tasks' | 'assets' | 'reports' | 'decisions'
      : 'dashboard';
    return companyPortalHref(context.resourceId, portalSurface);
  }
  if (context.kind === 'investor') return '/investor/dashboard';

  if (context.kind === 'partner' || context.kind === 'vendor') {
    const target = ACCOUNT_SURFACES.has(currentSurface)
      ? currentSurface as CustomerRouteTarget
      : 'dashboard';
    return customerRouteHref(options.workspaceId, target, {
      search: { context: context.key },
    });
  }

  const identityTarget = ACCOUNT_SURFACES.has(currentSurface)
    ? currentSurface as CustomerRouteTarget
    : 'dashboard';
  return customerRouteHref(options.workspaceId, identityTarget);
}
